import type { CourseVerification, VerificationCourse } from "./courseVerification";
import type { RaceRule } from "../utils/raceRules";
import type {
  SignupAuthMethod,
  SignupPlatform,
} from "../constants/accountAcquisition";
import type { RaceInsuranceAcceptance } from "./raceInsurance";
import type { VenueAmenity } from "./venueAmenities";

export type LocationSourceInformation = {
  isSimulatedBySoftware: boolean;
  isProducedByAccessory: boolean;
};

export type KompeteLocation = {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  horizontalAccuracy?: number | null;
  verticalAccuracy?: number | null;
  speed?: number | null;
  course?: number | null;
  timestamp?: Date | null;
  speedAccuracy?: number | null;
  courseAccuracy?: number | null;
  sourceInformation?: LocationSourceInformation | null;
};

export const EVENT_TYPES = [
  "Race",
  "Gathering",
  "Festival",
  "Workshop",
  "Meet Up",
  "Other",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_STATUSES = [
  "notStarted",
  "inProgress",
  "finished",
  "cancelled",
  "unknown",
] as const;

export type EventStatus = (typeof EVENT_STATUSES)[number];

export const EVENT_ENTRY_TYPES = ["Free", "Paid"] as const;

export type EventEntryType = (typeof EVENT_ENTRY_TYPES)[number];

export const REFUND_POLICY_TYPES = [
  "noRefunds",
  "untilDeadline",
  "caseByCase",
] as const;

export type RefundPolicyType = (typeof REFUND_POLICY_TYPES)[number];

export const EVENT_REGISTRATION_STATUSES = [
  "notOpen",
  "opensSoon",
  "open",
  "closed",
  "soldOut",
  "unknown",
] as const;

export type EventRegistrationStatus = (typeof EVENT_REGISTRATION_STATUSES)[number];

export const WORLD_TIME_ZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Europe/London",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Moscow",
  "Europe/Athens",
  "Africa/Cairo",
  "Africa/Nairobi",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Australia/Perth",
  "Australia/Adelaide",
  "Pacific/Auckland",
  "Pacific/Fiji",
  "UTC",
] as const;

export type WorldTimeZone = (typeof WORLD_TIME_ZONES)[number];

export const STRIPE_CONNECT_SUPPORTED_COUNTRIES = [
  "Albania",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Bahamas",
  "Bahrain",
  "Belgium",
  "Benin",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brunei",
  "Bulgaria",
  "Cambodia",
  "Canada",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cote d'Ivoire",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Estonia",
  "Ethiopia",
  "Finland",
  "France",
  "Gambia",
  "Germany",
  "Ghana",
  "Greece",
  "Guatemala",
  "Guyana",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kenya",
  "Kuwait",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Macao SAR China",
  "Madagascar",
  "Malta",
  "Mauritius",
  "Mexico",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Morocco",
  "Namibia",
  "Netherlands",
  "New Zealand",
  "Nigeria",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Panama",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Rwanda",
  "Saudi Arabia",
  "Saint Lucia",
  "Senegal",
  "Serbia",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "South Africa",
  "South Korea",
  "Spain",
  "Sri Lanka",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Tanzania",
  "Thailand",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vietnam",
] as const;

export const WORLD_COUNTRIES = STRIPE_CONNECT_SUPPORTED_COUNTRIES;

export type WorldCountry = (typeof STRIPE_CONNECT_SUPPORTED_COUNTRIES)[number];

export const RACE_DIFFICULTIES = ["Easy", "Moderate", "Hard", "Extreme"] as const;

export type RaceDifficulty = (typeof RACE_DIFFICULTIES)[number];

export const RACE_SURFACE_TYPES = [
  "Asphalt",
  "Dirt",
  "Gravel",
  "Sand",
  "Mixed",
  "Single Track",
  "Unknown",
] as const;

export type RaceSurfaceType = (typeof RACE_SURFACE_TYPES)[number];

export const RACE_START_POLICIES = ["scheduled", "userInitiated"] as const;

export type RaceStartPolicy = (typeof RACE_START_POLICIES)[number];

export const RACE_STATUSES = [
  "notStarted",
  "inProgress",
  "finished",
  "cancelled",
  "open",
  "unknown",
] as const;

export type RaceStatus = (typeof RACE_STATUSES)[number];

export const RACE_CATEGORIES = [
  "Women Open",
  "Men Open",
  "Coaching Class (8under)",
  "E-Bike",
  "Beginner Men Open",
  "Beginner Men 9-10",
  "Beginner Men 11-12",
  "Beginner Men 13-15",
  "Beginner Men 16-18",
  "Beginner Men 19-29",
  "Beginner Men 30-39",
  "Beginner Men 40-49",
  "Beginner Men 50-59",
  "Beginner Women Open",
  "Beginner Women 18",
  "Sport Men Open",
  "Sport Men 14",
  "Sport Men 15-18",
  "Sport Men 19-29",
  "Sport Men 30-39",
  "Sport Men 40-49",
  "Sport Men 50-59",
  "Sport Men 60+",
  "Sport Women Open",
  "Sport Women 24",
  "Sport Women 25+",
  "Expert Men Open",
  "Expert Men 14 & Under",
  "Expert Men 15-18",
  "Expert Men 19-29",
  "Expert Men 30-39",
  "Expert Men 40-49",
  "Expert Men 50-59",
  "Expert Women Open",
  "Pro Men",
  "Pro Women",
  "Pro Veteran",
  "Non Binary",
  "Open",
  "Undefined",
] as const;

export type RaceCategory = (typeof RACE_CATEGORIES)[number];

export const SPORT_TYPES = [
  "Road Bike",
  "Mountain Bike",
  "Gravel Bike",
  "E-Road Bike",
  "E-Mountain Bike",
  "E-Gravel Bike",
  "Run",
  "Trail Run",
  "Car",
  "Motorcycle",
  "Sail",
  "Swim",
  "Stand Up Paddle",
  "Backcountry Ski",
  "Nordic Ski",
  "Snowboard",
  "Skateboard",
  "Roller Blades",
  "Roller Skates",
  "Mixed",
] as const;

export type SportType = (typeof SPORT_TYPES)[number];

export const RACE_ENDURANCE_FORMATS = [
  "threeHours",
  "sixHours",
  "twelveHours",
  "twentyFourHours",
] as const;

export type RaceEnduranceFormat = (typeof RACE_ENDURANCE_FORMATS)[number];

export const RACE_FORMAT_KINDS = [
  "endurance",
  "startToFinish",
  "enduro",
  "downhill",
  "laps",
  "checkpoints",
  "scavengerHunt",
  "globalOpenTimeTrial",
  "openCourseRacing",
] as const;

export type RaceFormatKind = (typeof RACE_FORMAT_KINDS)[number];

export type RaceFormat = {
  kind: RaceFormatKind;
  enduranceType?: RaceEnduranceFormat | null;
  raw?: Record<string, unknown>;
};

export type RaceProximitySettings = {
  checkInRadiusMeters: number;
  checkpointRadiusMeters: number;
  raceCueRadiusMeters: number;
  aidStationRadiusMeters: number;
  finishRadiusMeters: number;
};

export const RACE_PROXIMITY_DEFAULTS: RaceProximitySettings = {
  checkInRadiusMeters: 35,
  checkpointRadiusMeters: 15,
  raceCueRadiusMeters: 50,
  aidStationRadiusMeters: 50,
  finishRadiusMeters: 25,
};

export const CHECKPOINT_RADIUS_RANGE_METERS = {
  min: 7,
  max: 120,
} as const;

export const USER_GENDERS = ["Male", "Female", "Non Binary", "Other", "unknown"] as const;

export type UserGender = (typeof USER_GENDERS)[number];

export const USER_GEAR_TYPES = [
  "Trail Mountain Bike",
  "Enduro Mountain Bike",
  "XC Mountain Bike",
  "Down County Mountain Bike",
  "Gravel Bike",
  "Road Bike",
  "Downhill Bike",
  "E-Bike",
  "E-Mountain Bike",
  "Running Shoes",
  "Trail Running Shoes",
  "Hiking Boots",
  "Climbing Shoes",
  "Roller Skates",
  "Roller Blades",
  "Other",
] as const;

export type UserGearType = (typeof USER_GEAR_TYPES)[number];

export const USER_RACE_STATUSES = [
  "Not Started",
  "Did Not Show",
  "Finished",
  "Did Not Finish On Time",
  "Incomplete",
  "Disqualified",
  "In Race",
  "Finished Last",
  "Abandoned",
  "Paused",
  "Cancelled",
  "Unknown",
] as const;

export type UserRaceStatus = (typeof USER_RACE_STATUSES)[number];

export const EMERGENCY_RELATIONSHIPS = [
  "Parent",
  "Spouse",
  "Sibling",
  "Friend",
  "Other",
] as const;

export type EmergencyRelationship = (typeof EMERGENCY_RELATIONSHIPS)[number];

export type Sponsor = {
  id: string;
  name: string;
  imageUrlString: string | null;
  webUrlString: string | null;
};

export type Stage = {
  id: string;
  stageName: string;
  stageLocations: KompeteLocation[];
  stageStartLocation: KompeteLocation | null;
  stageEndLocation: KompeteLocation | null;
};

export type Checkpoint = {
  id: string;
  name: string;
  number: number;
  location: KompeteLocation;
  radiusMeters: number | null;
  imageUrlString: string | null;
};

export type AidStation = {
  id: string;
  name: string;
  number: number;
  location: KompeteLocation;
  imagesUrls: string[];
};

export type RaceCue = {
  id: string;
  title: string;
  description: string;
  imageUrlString: string | null;
  audioFileUrlString: string | null;
  isActive: boolean;
  location: KompeteLocation;
};

export type Event = {
  id: string;
  organizer: string;
  organizerPhotoFirebaseURLString: string | null;
  name: string;
  shortDescription: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  registrationOpenDate: Date | null;
  registrationCloseDate: Date | null;
  city: string;
  state: string | null;
  province: string | null;
  country: string;
  address: string;
  zipCode: string | null;
  postalCode: string | null;
  geoHash: string | null;
  geoHashData: Record<string, string>;
  mobileCoverImageUrl?: string | null;
  webCoverImageUrl?: string | null;
  photoFirebaseURL: string | null;
  photosFirebaseURLs: string[];
  price: string;
  currency: string;
  entryType: EventEntryType;
  refundPolicyType: RefundPolicyType | null;
  refundDeadline: Date | null;
  isRefundable: boolean;
  maxAllowedParticipants: number | null;
  waitListIds: string[];
  isSoldOut: boolean;
  status: EventStatus;
  isDiscoverable: boolean;
  wasCancelled: boolean;
  isTest: boolean;
  eventTimeZone: WorldTimeZone;
  location: KompeteLocation | null;
  eventType: EventType;
  isFundRaiser: boolean;
  emailSubject: string;
  emailBody: string;
  sponsors: Sponsor[];
  venueAmenities?: VenueAmenity[];
  defaultOpenWindowStart: Date | null;
  defaultOpenWindowEnd: Date | null;
};

export type RacePaymentSettings = {
  provider: string | null;
  feePolicyVersion: string | null;
  platformFeeBps: number | null;
  organizerCommissionBps?: number | null;
  organizerShareBps?: number | null;
  racerServiceFeeBps?: number | null;
  pricingDisclosureVersion?: string | null;
};

export type RegistrationPricingSnapshot = {
  baseEntryAmount: number;
  racerServiceFeeAmount: number;
  totalAmount: number;
  platformCommissionAmount: number;
  organizerTransferAmount: number;
  platformRetainedAmount: number;
  currency: string;
  currencyExponent: number;
  organizerCommissionBps: number;
  platformFeeBps?: number;
  organizerShareBps: number;
  racerServiceFeeBps: number;
  feePolicyVersion: string | null;
  pricingDisclosureVersion: string | null;
  taxPolicy?: RegistrationTaxPolicy | null;
};

export type RegistrationTaxPolicyComponent = {
  liableParty: "organizer" | "rauxa" | string;
  taxability: "taxable" | "not_taxable" | "organizer_determined" | string;
  priceIncludesTax: boolean;
  taxCode: string | null;
};

export type RegistrationTaxRegistrationRule = {
  mode: "required" | "not_required" | "manual_review" | string;
  requiredTypes: string[];
  requiredRegistrations?: Array<{ type: string; jurisdiction: string }>;
};

export type RegistrationTaxPolicy = {
  version: string | null;
  integrationVersion: string | null;
  disclosureVersion: string | null;
  jurisdiction: string | null;
  determinationSource: string | null;
  configurationStatus: "ready" | "rauxa_collection_required" | string | null;
  reviewStatus?: "reviewed" | "pending_review" | "blocked" | string | null;
  registrationRule?: RegistrationTaxRegistrationRule | null;
  raceEntry: RegistrationTaxPolicyComponent;
  serviceFee: RegistrationTaxPolicyComponent;
};

export type RegistrationTaxQuoteBreakdown = {
  amount: number;
  taxableAmount: number;
  inclusive: boolean;
  country: string | null;
  state: string | null;
  taxType: string | null;
  percentageDecimal: string | null;
  taxabilityReason: string | null;
};

export type RegistrationTaxQuote = {
  version: string;
  disclosureVersion: string;
  calculationId: string | null;
  inputFingerprint: string | null;
  liableParty: "organizer" | "rauxa" | string;
  liableAccountId: string | null;
  currency: string;
  subtotalAmount: number;
  taxAmountExclusive: number;
  taxAmountInclusive: number;
  taxAmount: number;
  totalAmount: number;
  performanceLocationId: string | null;
  breakdown: RegistrationTaxQuoteBreakdown[];
  calculatedAt: Date | null;
  expiresAt: Date | null;
  source: "stripe_checkout_automatic_tax" | "stripe_tax_calculation" | string | null;
  status: string | null;
};

export type Race = {
  notifyOrganizerOnRegistration?: boolean;
  requireParticipantId?: boolean;
  courseVerificationEnabled?: boolean;
  courseVerificationCourse?: VerificationCourse | null;
  courseVerificationOffsets?: number[];
  event: Event;
  id: string;
  eventId: string;
  purchasesId: string;
  organizerUid: string | null;
  organizerEmail: string | null;
  notificationEmail: string | null;
  requestedDiscoverable: boolean | null;
  submissionStatus: "pending" | "approved" | "denied" | null;
  insurance?: RaceInsuranceAcceptance | null;
  startDate: Date | null;
  endDate: Date | null;
  sponsors: Sponsor[];
  venueAmenities?: VenueAmenity[];
  startLocation: KompeteLocation | null;
  endLocation: KompeteLocation | null;
  checkpoints: Checkpoint[];
  aidStations: AidStation[];
  aidStationLocations: KompeteLocation[];
  checkInLocation: KompeteLocation | null;
  difficulty: RaceDifficulty;
  rating: number | null;
  categories: RaceCategory[];
  leaderboard: Record<string, number>;
  rules: string[];
  ruleDetails?: RaceRule[];
  registeredUserIds: string[];
  checkedInUserIds: string[];
  didFinishIds: string[];
  didFinishIncompleteIds: string[];
  didNotFinishOnTimeIds: string[];
  didNotShowIds: string[];
  disqualifiedIds: string[];
  didFinishLastId: string | null;
  abandonedIds: string[];
  podiumIds: string[];
  winnerId: string | null;
  prizes: string[];
  totalLaps: number | null;
  checkInStartTime: Date | null;
  checkInCloseTime: Date | null;
  format: RaceFormat;
  sportType: SportType;
  gpxFileFirebaseURLString: string | null;
  podiumCeremonyDate: Date | null;
  waiver: string;
  raceMeetingTime: Date | null;
  stages: Stage[];
  length: number | null;
  surfaceType: RaceSurfaceType;
  totalAscent: number | null;
  totalDescent: number | null;
  isCheckInEnabled: boolean | null;
  targetDistance: number | null;
  isFlexibleRoute: boolean;
  allowsReEntry: boolean;
  minimumFinishTimeSeconds?: number;
  offlineResultSyncGraceHours?: number;
  raceCues: RaceCue[];
  enforceOffRoute: boolean;
  showsRacersLocations: boolean;
  shouldBypassLocationCheckIn: boolean;
  isCancelled: boolean;
  startPolicy: RaceStartPolicy;
  effectiveOpenWindowStart: Date | null;
  effectiveOpenWindowEnd: Date | null;
  routePolyline: KompeteLocation[];
  paymentSettings: RacePaymentSettings | null;
  pricing?: RegistrationPricingSnapshot | null;
  publishBlockReason: string | null;
  proximitySettings: RaceProximitySettings;
  resultsFinalizedAt: Date | null;
  resultsNotificationsCompletedAt: Date | null;
  /** Monotonic server fence used to invalidate stale local test-race attempts. */
  testResetGeneration?: number;
};

export type EmergencyContact = {
  name: string;
  phoneNumber: string;
  relationship: EmergencyRelationship;
};

export type GearItem = {
  id: string;
  type: UserGearType;
  model: string;
  totalTimeUsed: number;
  totalDistanceUsed: number;
};

export type StripeConnectStatus = {
  accountId: string | null;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  disabledReason: string | null;
  lastAccountUpdateAt: Date | null;
};

export type KompeteUser = {
  id: string;
  userName: string;
  email: string;
  appleIdToken: string | null;
  legalFirstName: string | null;
  legalLastName: string | null;
  birthDate: Date | null;
  gender: UserGender | null;
  bio: string | null;
  fcmToken: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  legalAddress: string | null;
  profilePhotoURLString: string | null;
  isRacing: boolean;
  didAgreeToTermsAndConditions: boolean;
  registeredRacesIds: string[];
  bookmarkedRacesIds: string[];
  friendUserIds: string[];
  blockedUserIds: string[];
  followingUserIds: string[];
  isBannedFromRacing: boolean;
  gear: GearItem[];
  emergencyContact: EmergencyContact | null;
  totalDistanceRaced: number;
  totalRacesFinished: number;
  totalRacesWon: number;
  createdRaceIds: string[];
  isAppleTester: boolean;
  registrationDate: Date | null;
  /** Missing on legacy profiles; report it as unknown rather than inferring it. */
  signupPlatform?: SignupPlatform | null;
  /** Authentication method is independent from the client platform. */
  signupAuthMethod?: SignupAuthMethod | null;
  acceptedOrganizerPayoutTerms: boolean;
  organizerPayoutTermsAcceptedAt: Date | null;
  organizerPayoutTermsVersion: string | null;
  stripeConnect: StripeConnectStatus | null;
};

export type UserRace = {
  courseVerificationRequired?: boolean;
  courseVerificationCourse?: VerificationCourse | null;
  courseVerification?: CourseVerification | null;
  id: string;
  name: string;
  userId: string;
  raceId: string;
  category: RaceCategory;
  format: RaceFormat;
  sportType: SportType;
  plateNumber: number | null;
  lapNumber: number | null;
  totalLaps: number | null;
  lapsTime: Record<string, Date>;
  raceTime: number | null;
  finalPosition: number | null;
  raceStartLocation: KompeteLocation | null;
  raceEndLocation: KompeteLocation | null;
  attemptEndLocation: KompeteLocation | null;
  currentUserLocation: KompeteLocation | null;
  locations: KompeteLocation[];
  aidStationLocations: KompeteLocation[];
  checkpointLocations: KompeteLocation[];
  checkpointReachedIds: string[];
  stages: Stage[];
  status: UserRaceStatus;
  startDate: Date | null;
  checkInStartTime: Date | null;
  checkInCloseTime: Date | null;
  didCheckIn: boolean;
  checkInUserTime: Date | null;
  isTest: boolean;
  stageStartTime: Date | null;
  gpxFileFirebaseURLString: string | null;
  endDate: Date | null;
  garminInReachSharePageURLString: string | null;
  spotSharePageURLString: string | null;
  finishedTime: number | null;
  averageSpeed: number | null;
  elevationGained: number | null;
  totalDistance: number | null;
  caloriesBurned: number | null;
  gearUsed: GearItem | null;
  userStartTime: Date | null;
  userEndTime: Date | null;
  attemptNumber: number | null;
  countdownMovementWarningCount: number;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  startPolicy: RaceStartPolicy | null;
  /** Generation of the authoritative race when this attempt was registered. */
  testResetGeneration?: number;
};

export type RacerLocation = {
  id: string;
  userId: string;
  userName: string;
  userProfileImageUrlString: string | null;
  lastUpdated: Date | null;
  location: KompeteLocation;
  status: UserRaceStatus;
  checkpointReachedIds?: string[];
  testResetGeneration?: number;
};

export type LegalDisclaimer = {
  user: KompeteUser;
  date: Date | null;
  didAgreeToTerms: boolean;
};

export type FeatureFlags = {
  shouldEnableAppleSignIn: boolean;
  shouldShowColorSchemeOption: boolean;
  shouldUseLocationToFetchRaces: boolean;
};

export type RaceResult = {
  id: string;
  userName: string;
  userId: string;
  profileImageUrlString: string | null;
  position: number | null;
  userRace: UserRace;
  finalResultsComputedAt: Date | null;
};

export const REGISTRATION_STATUSES = [
  "initiated",
  "paymentPending",
  "confirmed",
  "paymentFailed",
  "refunded",
  "canceled",
] as const;

export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];

export const REFUND_STATUSES = ["none", "partial", "full"] as const;

export type RefundStatus = (typeof REFUND_STATUSES)[number];

export type RegistrationRecord = {
  id: string;
  raceId: string;
  raceName: string;
  organizerId: string;
  participantUserId: string;
  userRaceId: string;
  plateNumber?: number | null;
  stripeAccountId: string | null;
  status: RegistrationStatus;
  amount: number;
  currency: string;
  platformFeeBps: number;
  applicationFeeAmount: number;
  feePolicyVersion: string | null;
  pricing?: RegistrationPricingSnapshot | null;
  paymentFlowVersion?: string | null;
  taxQuote?: RegistrationTaxQuote | null;
  taxTransactionId?: string | null;
  taxTransactionStatus?: string | null;
  paymentIntentId: string | null;
  checkoutSessionId: string | null;
  chargeId: string | null;
  refundStatus: RefundStatus;
  refundedAmount: number;
  participantProfileSnapshot: Record<string, unknown>;
  raceCategory: RaceCategory | string;
  gearUsed: GearItem | null;
  paymentConfirmedAt: Date | null;
  refundedAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
};
